<template>
  <div class="formester">
    <OfflineSubmissionSync
      v-if="!isSubmitted"
      :offline-submission-count="offlineSubmissionCount"
      :styling="styling"
      :syncing="syncingSubmissions"
      @sync-submissions="handleSyncSubmission"
    />

    <PgLoader
      :loading="loading || !formLoaded"
      :color="primaryColor"
      class="fullscreen-loader"
    />

    <FormesterForm
      v-if="!loading"
      ref="formesterForm"
      :is-iframe="isIframe"
      :action="postUrl"
      :form="formBuilder"
      :styling="styling"
      :settings="formData.accessSettings"
      :is-preview="isPreview"
      :is-edit-mode="editMode"
      :uuid="uuid"
      :form-id="formId"
      :prefill="prefill"
      :is-free-user="isFreeUser"
      :has-trusted-form-certificate="hasTrustedFormCertificate"
      :rules="rules"
      :scripts="scripts"
      :variables="variables"
      :translations="translations"
      :pages="pages"
      :session-id="sessionId"
      :survey-url="formData.surveyUrl"
      :pending-submissions="pendingSubmissions"
      :pending-submission-columns="pendingSubmissionColumns"
      :offline-submission-count="offlineSubmissionCount"
      :view-pending-submissions="viewPendingSubmissions"
      :allowed-role-ids="allowedRoleIds"
      :allow-all-fields="allowAllFields"
      :form-executable="formData.formExecutable"
      @submit="handleFormSubmit"
      @form-loaded="formLoaded = $event"
    />

    <TrackingIntegrations
      v-if="!isEmpty(formData.trackingIntegrations)"
      :form-id="formId"
      :form-uuid="formData.uuid"
      :form-name="formData.name"
      :integrations="formData.trackingIntegrations"
    />

    <InstallAppBanner
      v-if="styling.allowOfflineMode"
      ref="installAppBanner"
      :styling="styling"
    />
  </div>
</template>

<script>
import { isEmpty } from "lodash-es";
import { ElNotification } from "element-plus";
import { networkStatus } from "@/utils/checkNetworkStatus.js";
import FormesterAnalytics from "@/utils/formester-analytics.js";
import VisibilityTracker from "@/utils/visibility-tracker.js";
import OfflineModeFormController from "@/controllers/pwa/offline_mode_form_controller";
import { getFormPrefill } from "@/api/form_prefill";
import { getSubmissionData } from "@/api/submissions";
import { getFieldId } from "@/helpers/utils";
import INTERNAL_QUERY_PARAMS from "@/constants/internalQueryParams";

// Components
import PgLoader from "@/components/shared/loading.vue";
import FormesterForm from "@/components/builder/formester-form.vue";
import TrackingIntegrations from "@/components/tracking_integrations/index.vue";
import InstallAppBanner from "./install-app-banner.vue";
import OfflineSubmissionSync from "./offline-submission-sync.vue";

// Generate Metatags
import getSiteMeta from "@/helpers/getSiteMeta.js";

// composables
import { useFormesterSession } from "@/helpers/composables/use-formester-session";
import { useFormStore } from "@/stores/form";
import { useFormLoader } from "@/helpers/composables/use-form-loader";
import { useFormSubmissionHandler } from "@/helpers/composables/use-form-submission-handler";
import { storeToRefs } from "pinia";

export default {
  components: {
    FormesterForm,
    PgLoader,
    TrackingIntegrations,
    InstallAppBanner,
    OfflineSubmissionSync,
  },
  provide() {
    return {
      formesterAnalytics: this.formesterAnalytics,
    };
  },
  props: {
    formId: {
      type: String,
      required: true,
    },
    uuid: {
      type: String,
      required: true,
    },
    primaryColor: {
      type: String,
      required: true,
    },
    backgroundColor: {
      type: String,
      required: true,
    },
    formDataResponse: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const { sessionId, clearSession } = useFormesterSession();

    // Initialize FormesterAnalytics with proper uuid from props
    // Note: sessionId is a computed ref, so we need sessionId.value
    const formesterAnalytics = new FormesterAnalytics(
      sessionId.value,
      props.uuid,
    );

    const formStore = useFormStore();
    formStore.setSessionId(sessionId.value);

    const {
      formData,
      formBuilder,
      styling,
      rules,
      scripts,
      variables,
      translations,
      pages,
      isFreeUser,
      hasTrustedFormCertificate,
    } = storeToRefs(formStore);

    const formLoader = useFormLoader();
    const submissionHandler = useFormSubmissionHandler();

    return {
      sessionId,
      clearSession,
      formesterAnalytics,
      formStore,
      formData,
      formBuilder,
      styling,
      rules,
      scripts,
      variables,
      translations,
      pages,
      isFreeUser,
      hasTrustedFormCertificate,
      ...formLoader,
      ...submissionHandler,
    };
  },
  data() {
    return {
      prefill: {},
      formController: null,
      offlineSubmissionCount: 0,
      offlineSubmissionCountObserver: null,
      isOnline: true,
      syncingSubmissions: false,
      isPreview: false,
      pendingSubmissions: [],
      pendingSubmissionColumns: [],
      viewPendingSubmissions: false,
      allowedRoleIds: [],
      allowAllFields: false,
      formLoaded: false,
      visibilityTracker: null,
      initialViewTracked: false,
      iframeVisibleFromParent: false,
    };
  },
  computed: {
    postUrl() {
      return `/forms/${this.uuid}/submissions.json`;
    },
    isIframe() {
      return parent.location !== window.location;
    },
    meta() {
      const metaData = {
        type: "website",
        url: `https://app.formester.com/f/${this.uuid}`,
        title: this.formData.name,
        description: "Fill out this form to get started",
        mainImage:
          "https://formester.com/formester-form-builder-background.png", // need to update with survey page image
        mainImageAlt: "Form builder showing drag and drop functionality", // need to update with survey page image alt
      };
      return getSiteMeta(metaData);
    },
    editMode() {
      const query = new URLSearchParams(window.location.search);
      return !!query.get(INTERNAL_QUERY_PARAMS.edit);
    },
  },
  async created() {
    // prefill value from query parameter
    const query = new URLSearchParams(window.location.search);

    // used for rendering survey forms as preview in embed page
    this.isPreview = query.get(INTERNAL_QUERY_PARAMS.preview);

    for (const [key, value] of query.entries()) {
      if (value) {
        this.prefill[key] = value;
      }
    }

    const params = { session_id: this.sessionId };
    if (this.isPreview) {
      params[INTERNAL_QUERY_PARAMS.preview] = true;
    }

    this.loadFormData();
  },
  async mounted() {
    // Listen for iframe visibility messages from parent window
    if (this.isIframe) {
      this.setupIframeVisibilityListener();
    }

    window.addEventListener("beforeinstallprompt", (event) => {
      this.$nextTick(() => {
        if (this.$refs.installAppBanner) {
          this.$refs.installAppBanner?.onBeforeInstallPrompt(event);
        } else {
          // fallback if child isn't ready yet
          this.waitForInstallBanner(event);
        }
      });
    });

    const query = new URLSearchParams(window.location.search);
    const prefillUuid = query.get("_prefill");
    const editSubmission = query.get(INTERNAL_QUERY_PARAMS.edit);

    if (editSubmission) {
      await this.getFormSubmissionData(editSubmission);
    } else if (prefillUuid) {
      await this.getFormPrefillData(prefillUuid);
    }

    // Show thank-you page
    const showFormClosedPage = this.pages.find((page) => page.formClosedError);
    if (
      (query.has("payment_status") && query.has("payment_id")) ||
      showFormClosedPage
    ) {
      this.handleSubmit({ thankYouPageId: showFormClosedPage?.id });
    }

    // Offline form
    this.handleOfflineForm();

    // Initialize visibility tracking
    this.initializeVisibilityTracking();
  },
  beforeUnmount() {
    this.offlineSubmissionCountObserver?.unsubscribe();
    networkStatus.destroy();
    this.visibilityTracker?.destroy();
    window.removeEventListener("message", messageHandler);
  },
  methods: {
    isEmpty,
    handleFormSubmit(e) {
      // reset session id after submission
      this.clearSession();
      this.handleSubmit(e);
    },
    loadFormData() {
      this.loading = true;
      try {
        const data = JSON.parse(this.formDataResponse);
        this.loadFromJson(data);

        // Set survey-specific properties (not stored in main form data)
        this.pendingSubmissions = data.pendingSubmissions || [];
        this.pendingSubmissionColumns = data.submissionColumns || [];
        this.viewPendingSubmissions = data.viewPendingSubmissions || false;
        this.allowedRoleIds = data.allowedRoleIds || [];
        this.allowAllFields = data.allowAllFields || false;
      } catch (error) {
        console.log("Not able to get data");
      } finally {
        this.loading = false;
      }
    },
    async handleSyncSubmission() {
      if (this.syncingSubmissions) return;

      await networkStatus.checkOnlineStatus();
      if (
        networkStatus.online &&
        this.offlineSubmissionCount > 0 &&
        !this.syncingSubmissions
      ) {
        this.syncingSubmissions = true;
        await this.formController.syncSubmissions();
        this.syncingSubmissions = false;
      }
    },
    handleOfflineForm() {
      // Return if offline mode is not allowed or form is in iframe
      if (!this.styling.allowOfflineMode || this.isIframe) return;

      // Custom class to check network status
      networkStatus.addEventListener("status", async (status) => {
        this.isOnline = status;
        if (status && !this.syncingSubmissions) {
          this.syncingSubmissions = true;
          await this.formController.syncSubmissions();
          this.syncingSubmissions = false;
        }
      });

      // Observe submission count from indexed db and update offlineSubmissionCount if changed
      this.formController = new OfflineModeFormController();
      this.offlineSubmissionCountObserver = this.formController
        .observeSubmissionCount()
        .subscribe((count) => (this.offlineSubmissionCount = count));

      // Service worker inbuilt method "SyncManager" emits this event (Its in experimental stage and does not support many browsers)
      navigator.serviceWorker?.addEventListener("message", async (event) => {
        if (event.data.type === "backgroundSync" && !this.syncingSubmissions) {
          this.syncingSubmissions = true;
          await this.formController.syncSubmissions();
          this.syncingSubmissions = false;
        }
      });

      window.addEventListener("message", async (event) => {
        if (event.data.type === "synced") {
          ElNotification({
            message: "Submissions synced successfully",
            type: "success",
            position: "bottom-right",
          });
        } else if (event.data.type === "sync-progress") {
          ElNotification({
            message: "Syncing submissions in progress",
            type: "info",
            position: "bottom-right",
          });
        }
      });
    },
    async getFormPrefillData(prefillUuid) {
      try {
        const {
          data: { data },
        } = await getFormPrefill(this.formId, prefillUuid);
        for (const { id, value } of data) {
          const fieldId = getFieldId(id);
          const field = this.formData.formBuilder.find(
            (el) => el.id === fieldId,
          );

          if (field?.type === "matrix") {
            let valueObj = {};
            const rowId = id.split("_")[1];
            const colIds = Array.isArray(value) ? value : [value];
            valueObj[rowId] = {};
            colIds.forEach((colId) => {
              valueObj[rowId][colId] = true;
            });
            this.prefill[fieldId] = valueObj;
          } else {
            this.prefill[id] = value;
          }
        }
      } catch (error) {
        ElNotification({
          message:
            error.response?.data.message || "Unable to fetch prefill data",
          type: "error",
          position: "bottom-right",
        });
      }
    },
    async getFormSubmissionData(editToken) {
      try {
        this.loading = true;
        const { data } = await getSubmissionData(this.formId, editToken);
        this.prefill = data;
      } catch (error) {
        ElNotification({
          message:
            error.response?.data.message || "Unable to fetch submission data",
          type: "error",
          position: "bottom-right",
        });
      } finally {
        this.loading = false;
      }
    },
    waitForInstallBanner(event) {
      const interval = setInterval(() => {
        if (this.$refs.installAppBanner) {
          this.$refs.installAppBanner?.onBeforeInstallPrompt(event);
          clearInterval(interval);
        }
      }, 500); // Check every 500ms
    },
    initializeVisibilityTracking() {
      if (this.isPreview || this.initialViewTracked) return;

      this.visibilityTracker = new VisibilityTracker();

      // Wait for form to be loaded before setting up visibility tracking
      this.$nextTick(() => {
        this.setupFormVisibilityTracking();
      });
    },
    setupFormVisibilityTracking() {
      const formElement = this.$el;
      if (!formElement) return;

      // check if its iframe and iframe is visible
      if (this.isIframe && !this.iframeVisibleFromParent) {
        return;
      }

      // Check if form is already visible
      if (VisibilityTracker.isElementVisible(formElement)) {
        this.handleFormVisible();
        return;
      }

      // Set up intersection observer for when form becomes visible
      this.visibilityTracker.track(formElement, () => {
        this.handleFormVisible();
      });
    },
    setupIframeVisibilityListener() {
      const messageHandler = (event) => {
        if (
          event.data?.type === "formester-iframe-visible" &&
          event.data?.visible
        ) {
          this.iframeVisibleFromParent = true;
          this.setupFormVisibilityTracking();
          this.handleFormVisible();
        }
      };

      window.addEventListener("message", messageHandler);
    },
    handleFormVisible() {
      if (this.initialViewTracked || this.isPreview) return;

      this.initialViewTracked = true;
      const firstPageId = this.pages.length > 0 ? this.pages[0].id : null;

      if (this.formesterAnalytics && firstPageId) {
        this.formesterAnalytics.trackEvent(firstPageId, "form_view");
      }
    },
  },

  metaInfo() {
    return {
      meta: [
        ...(this.meta || []),
        { charset: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1" },
        {
          name: "robots",
          content: "index, follow",
        },
        { name: "format-detection", content: "telephone=no" },
      ],
      link: [
        { rel: "icon", type: "image/x-icon", href: "/favicon.ico" },
        {
          hid: "canonical",
          rel: "canonical",
          href: `https://app.formester.com/f/${this.uuid}`,
        },
      ],
    };
  },
};
</script>

<style>
@import "@/assets/styles/formester-form.css";

:root {
  --star-color: "";
  --scale-background-color: "";
  --placeholder-text-color: "";
}

.non-editing {
  padding: 10px 16px;
  border-radius: 5px;
  position: relative;
  box-sizing: border-box;
}
.non-editing.child-component {
  padding: 6px 8px;
}

.left-right {
  display: flex;
}

.left-right > label {
  width: 200px;
  flex-shrink: 0;
}

.formester {
  width: 100%;
  background: v-bind(backgroundColor);
}

.survey-form-erb {
  height: auto;
}

.formester-required::after {
  color: #f55753;
  content: " *";
  font-family: arial;
  font-size: 15px;
}

.spinner-container {
  width: 100%;
  min-height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.spin {
  width: 40px;
  height: 40px;
}

@media only screen and (max-width: 768px) {
  .non-editing {
    padding: 6px 12px !important;
  }
}

.fullscreen-loader {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: v-bind(backgroundColor);
  z-index: 9999;
}
</style>
